import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, Sparkles, MapPin, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BookSiteWalkModal — a 3-step, zero-friction lead capture wizard.
 * Step 1: property address / market
 * Step 2: primary service interest
 * Step 3: urgency + contact
 * On submit → POST /api/leads with source="site-walk-wizard".
 *
 * Trigger: floating pill button ("Book a Site Walk · 30s") at bottom-center on desktop.
 */
const SERVICES = [
  { key: "windows",  label: "Window cleaning", icon: "◇" },
  { key: "pressure", label: "Pressure washing", icon: "≋" },
  { key: "awning",   label: "Awning cleaning", icon: "⌂" },
  { key: "sidewalk", label: "Sidewalk / concrete", icon: "▢" },
  { key: "graffiti", label: "Graffiti removal", icon: "×" },
  { key: "lighting", label: "Outdoor lighting", icon: "✧" },
  { key: "portfolio", label: "Full portfolio walk", icon: "★" },
  { key: "other",    label: "Something else", icon: "…" },
];

const URGENCY = [
  { key: "asap",    label: "This week", tone: "hot" },
  { key: "month",   label: "Within a month", tone: "warm" },
  { key: "quarter", label: "Within a quarter", tone: "cool" },
  { key: "explore", label: "Just exploring", tone: "cold" },
];

export default function BookSiteWalkModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    address: "",
    market: "NYC",
    service: "",
    urgency: "",
    name: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setDone(false); }
  }, [open]);

  function set(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  const canAdvance =
    (step === 1 && (form.address.trim().length > 3 || form.market)) ||
    (step === 2 && form.service) ||
    (step === 3 && form.urgency && form.name.trim() && (form.email.trim() || form.phone.trim()));

  async function submit() {
    setSubmitting(true);
    try {
      const svc = SERVICES.find((s) => s.key === form.service);
      const urg = URGENCY.find((u) => u.key === form.urgency);
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: svc?.label || form.service,
        property_type: form.address || form.market,
        message: `[SiteWalk Wizard] Market: ${form.market} · Address: ${form.address || "n/a"} · Urgency: ${urg?.label || form.urgency}`,
        source: "site-walk-wizard",
      });
      setDone(true);
      toast.success("Site walk requested. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try phone or email.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
          data-testid="sitewalk-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0" style={{
            background: "rgba(2,2,4,0.72)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }} />

          {/* Modal card */}
          <motion.div
            className="relative w-full max-w-[720px] max-h-[92vh] overflow-y-auto"
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(160deg, rgba(20,20,24,0.92), rgba(10,10,13,0.94))",
              border: "1px solid var(--line-gold)",
              borderRadius: "28px",
              boxShadow: "0 60px 120px -30px rgba(0,0,0,0.9), 0 0 0 1px rgba(198,168,124,0.08) inset",
            }}
            data-testid="sitewalk-modal"
          >
            {/* Ambient glow */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(600px 300px at 80% 0%, rgba(198,168,124,0.12), transparent 60%)",
              borderRadius: "28px",
            }} />

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center transition-all hover:rotate-90"
              style={{
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "var(--text-2)",
                background: "rgba(255,255,255,0.03)",
              }}
              data-testid="sitewalk-close"
              aria-label="Close"
            >
              <X size={16} strokeWidth={1.5} />
            </button>

            <div className="relative p-8 md:p-12">
              {/* Header */}
              <div className="overline mb-4">Book a Site Walk · 30 seconds</div>

              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="py-8 text-center"
                  data-testid="sitewalk-done"
                >
                  <div className="w-16 h-16 mx-auto flex items-center justify-center mb-6" style={{
                    borderRadius: "999px",
                    background: "rgba(74,222,128,0.14)",
                    border: "1px solid rgba(74,222,128,0.4)",
                  }}>
                    <Check size={22} style={{ color: "#4ade80" }} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-4xl md:text-5xl" style={{ letterSpacing: "-0.03em" }}>
                    Site walk <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>booked.</span>
                  </h3>
                  <p className="mt-6 font-body max-w-md mx-auto" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
                    An account lead will reach out within one business hour to confirm the on-site visit and give you your Certificate of Insurance.
                  </p>
                  <button
                    onClick={onClose}
                    className="btn-glow mt-10 inline-flex"
                    data-testid="sitewalk-close-done"
                  >
                    Close <ArrowRight size={13} />
                  </button>
                </motion.div>
              ) : (
                <>
                  {/* Progress rail */}
                  <div className="mb-8 flex items-center gap-3">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="flex-1 h-[3px]" style={{
                        background: n <= step ? "var(--gold)" : "rgba(255,255,255,0.08)",
                        boxShadow: n <= step ? "0 0 8px var(--gold)" : "none",
                        borderRadius: 2,
                        transition: "all 500ms ease",
                      }} />
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    {step === 1 && (
                      <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                        <div className="flex items-center gap-3 mb-3">
                          <MapPin size={16} style={{ color: "var(--gold)" }} />
                          <h3 className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.03em" }}>Where's the property?</h3>
                        </div>
                        <p className="font-body mb-8" style={{ color: "var(--text-2)" }}>Address or intersection is fine. We only need enough to route a crew.</p>
                        <input
                          value={form.address}
                          onChange={(e) => set("address", e.target.value)}
                          placeholder="e.g. 452 Broadway, SoHo NYC"
                          className="w-full px-5 py-4 font-body text-lg mb-5"
                          style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "14px",
                            color: "var(--text)",
                          }}
                          data-testid="sitewalk-address"
                          autoFocus
                        />
                        <div className="overline mb-3">Market</div>
                        <div className="grid grid-cols-2 gap-3">
                          {["NYC", "Charlotte, NC"].map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => set("market", m)}
                              className="p-4 text-left transition-all"
                              style={{
                                background: form.market === m ? "rgba(198,168,124,0.14)" : "rgba(255,255,255,0.03)",
                                border: form.market === m ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "14px",
                                color: form.market === m ? "var(--gold-2)" : "var(--text)",
                              }}
                              data-testid={`sitewalk-market-${m.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            >
                              <div className="font-display text-xl" style={{ letterSpacing: "-0.02em" }}>{m}</div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                        <div className="flex items-center gap-3 mb-3">
                          <Sparkles size={16} style={{ color: "var(--gold)" }} />
                          <h3 className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.03em" }}>What are you tackling first?</h3>
                        </div>
                        <p className="font-body mb-8" style={{ color: "var(--text-2)" }}>Pick one — we'll bring the right crew and equipment to the site walk.</p>
                        <div className="grid grid-cols-2 gap-3">
                          {SERVICES.map((s) => (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => set("service", s.key)}
                              className="p-4 text-left transition-all flex items-center gap-3"
                              style={{
                                background: form.service === s.key ? "rgba(198,168,124,0.14)" : "rgba(255,255,255,0.03)",
                                border: form.service === s.key ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "14px",
                                color: form.service === s.key ? "var(--gold-2)" : "var(--text)",
                              }}
                              data-testid={`sitewalk-service-${s.key}`}
                            >
                              <span className="text-lg opacity-70">{s.icon}</span>
                              <span className="font-body">{s.label}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
                        <div className="flex items-center gap-3 mb-3">
                          <Zap size={16} style={{ color: "var(--gold)" }} />
                          <h3 className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.03em" }}>When and who?</h3>
                        </div>
                        <p className="font-body mb-6" style={{ color: "var(--text-2)" }}>Urgency helps us slot the site walk. And we'll need one way to reach you.</p>

                        <div className="overline mb-3">Urgency</div>
                        <div className="grid grid-cols-2 gap-3 mb-8">
                          {URGENCY.map((u) => (
                            <button
                              key={u.key}
                              type="button"
                              onClick={() => set("urgency", u.key)}
                              className="p-3 transition-all"
                              style={{
                                background: form.urgency === u.key ? "rgba(198,168,124,0.14)" : "rgba(255,255,255,0.03)",
                                border: form.urgency === u.key ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "12px",
                                color: form.urgency === u.key ? "var(--gold-2)" : "var(--text)",
                              }}
                              data-testid={`sitewalk-urgency-${u.key}`}
                            >
                              <div className="font-body">{u.label}</div>
                            </button>
                          ))}
                        </div>

                        <input
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                          placeholder="Your name"
                          className="w-full px-5 py-3.5 font-body mb-3"
                          style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "12px",
                            color: "var(--text)",
                          }}
                          data-testid="sitewalk-name"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="Email"
                            type="email"
                            className="w-full px-5 py-3.5 font-body"
                            style={{
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: "12px",
                              color: "var(--text)",
                            }}
                            data-testid="sitewalk-email"
                          />
                          <input
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            placeholder="Phone"
                            type="tel"
                            className="w-full px-5 py-3.5 font-body"
                            style={{
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: "12px",
                              color: "var(--text)",
                            }}
                            data-testid="sitewalk-phone"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Nav */}
                  <div className="mt-10 flex items-center justify-between">
                    <button
                      onClick={() => setStep((s) => Math.max(1, s - 1))}
                      className="font-mono text-[10px] uppercase tracking-[0.36em] px-4 py-2.5 transition-colors"
                      style={{
                        color: step === 1 ? "var(--text-3)" : "var(--text-2)",
                        opacity: step === 1 ? 0.4 : 1,
                        pointerEvents: step === 1 ? "none" : "auto",
                      }}
                      data-testid="sitewalk-back"
                    >
                      ← Back
                    </button>
                    <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                      Step {step} / 3
                    </div>
                    {step < 3 ? (
                      <button
                        onClick={() => canAdvance && setStep((s) => Math.min(3, s + 1))}
                        disabled={!canAdvance}
                        className="btn-glow"
                        style={{ opacity: canAdvance ? 1 : 0.5 }}
                        data-testid="sitewalk-next"
                      >
                        Next <ArrowRight size={13} />
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={!canAdvance || submitting}
                        className="btn-glow"
                        style={{ opacity: !canAdvance || submitting ? 0.5 : 1 }}
                        data-testid="sitewalk-submit"
                      >
                        {submitting ? "Sending…" : "Book Walk"} <ArrowRight size={13} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
